/* =========================================================
   Automatic-discount price preview
   ---------------------------------------------------------
   Shopify native automatic discounts (Admin → Discounts →
   Automatic) are applied at cart/checkout — they do NOT change
   product.price on the storefront. So we discover the real
   discounted price + the discount title at runtime by previewing
   a cart via the Storefront API (cartCreate), then paint:
     • the listing card (struck-through regular price, discounted
       "starting at" price, and a sale-title label), and
     • the product page (discounted price + title label),
       repainting after Dawn re-renders the price on variant change.

   Server-rendered regular price stays as the no-JS fallback.

   Config comes from window.GTWDiscount (set in theme.liquid).
   No discount values live in the theme — Admin Discounts is the
   single source of truth.
   ========================================================= */
(function () {
  'use strict';

  var CFG = window.GTWDiscount || {};
  var PROXY_URL = CFG.proxyUrl || '';
  var MONEY_FMT = CFG.moneyFormat || '${{amount}}';

  if (!PROXY_URL) return; // no proxy configured → SSR fallback is what the customer sees

  // variantId(string) -> { total, subtotal, label } | null (no discount)
  var cache = Object.create(null);
  var pending = Object.create(null);

  // ---- money formatting (mirrors Shopify money_format placeholders) ----
  function thousands(num, sep) {
    var parts = num.toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return parts.join('.');
  }
  function formatMoney(cents) {
    var dollars = cents / 100;
    return MONEY_FMT
      .replace(/\{\{\s*amount\s*\}\}/g, thousands(dollars, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(dollars).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
        thousands(dollars, '.').replace(/\.(\d\d)$/, ',$1'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        Math.round(dollars).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  // Preview a batch of variant ids in one request via the discount-preview
  // worker (which holds the Shopify credentials server-side and runs cartCreate).
  // Resolves a map { variantId: { total, subtotal, label } } in integer cents.
  function previewBatch(ids) {
    return fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ variantIds: ids })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (!res || res.ok !== true || !res.prices) {
          console.warn('[auto-discount] proxy returned no prices:', res && res.error);
          return {};
        }
        return res.prices;
      })
      .catch(function (err) {
        console.warn('[auto-discount] proxy fetch failed:', err);
        return {};
      });
  }

  // Get the preview for a single variant, using/filling the cache.
  function previewOne(id) {
    id = String(id);
    if (cache[id] !== undefined) return Promise.resolve(cache[id]);
    if (pending[id]) return pending[id];
    var p = previewBatch([id]).then(function (map) {
      cache[id] = map[id] || null;
      delete pending[id];
      return cache[id];
    });
    pending[id] = p;
    return p;
  }

  // ---------- painting ----------

  // Paint a [data-auto-discount] container. `data` = { total, subtotal, label }.
  // `baseCents` is the SSR price we compare against (the regular price shown).
  function paint(el, baseCents, data) {
    if (!data || !data.total || data.total >= baseCents) return; // no real discount
    var saved = baseCents - data.total;
    var nowEl = el.querySelector('[data-ad-now]');
    var wasEl = el.querySelector('[data-ad-was]');
    var labelEl = document.querySelector(
      '[data-ad-label][data-ad-label-for="' + el.getAttribute('data-variant-id') + '"]'
    ) || el.parentNode && el.parentNode.querySelector('[data-ad-label]')
      || document.querySelector('[data-ad-label][data-ad-label-for="' + (el.getAttribute('data-ad-scope') || '') + '"]');

    // "From " prefix on listing cards where the price varies (matches Dawn).
    var fromPrefix = el.getAttribute('data-ad-from') || '';
    if (nowEl) nowEl.textContent = fromPrefix + formatMoney(data.total);
    if (wasEl) wasEl.textContent = formatMoney(baseCents);
    el.classList.add('ad-has-discount');
    el.hidden = false; // reveal the preview price block
    // Hide the server-rendered (regular) price within the same wrapper.
    var wrap = el.closest('[data-ad-wrap]');
    if (wrap) wrap.classList.add('ad-active');

    // Autogenerated label (only when not manually set by the merchant).
    if (labelEl && labelEl.getAttribute('data-ad-manual') !== 'true') {
      var title = data.label || 'Sale';
      labelEl.textContent = title;
      labelEl.hidden = false;
    }
    el.dispatchEvent(new CustomEvent('auto-discount:painted', {
      bubbles: true,
      detail: { total: data.total, base: baseCents, saved: saved, label: data.label }
    }));
  }

  // ---------- listing cards (batched) ----------
  function paintCards() {
    var nodes = Array.prototype.slice.call(
      document.querySelectorAll('[data-auto-discount][data-variant-id]:not([data-ad-pdp])')
    );
    if (!nodes.length) return;

    // unique ids -> elements (several cards can share a variant)
    var byId = Object.create(null);
    var order = [];
    nodes.forEach(function (el) {
      var id = el.getAttribute('data-variant-id');
      if (!id) return;
      if (!byId[id]) { byId[id] = []; order.push(id); }
      byId[id].push(el);
    });
    if (!order.length) return;

    // Use cache where we have it; only request the unknowns.
    var unknown = order.filter(function (id) { return cache[id] === undefined; });
    var ready = unknown.length ? previewBatch(unknown) : Promise.resolve({});
    ready.then(function (map) {
      order.forEach(function (id) {
        if (cache[id] === undefined) cache[id] = map[id] || null;
        var data = cache[id];
        if (!data) return;
        byId[id].forEach(function (el) {
          var base = parseInt(el.getAttribute('data-base-price'), 10);
          if (!isFinite(base)) base = data.subtotal;
          paint(el, base, data);
        });
      });
    });
  }

  // ---------- product page (single variant, repaint on change) ----------
  function paintPdp(el) {
    if (!el) return;
    var id = el.getAttribute('data-variant-id');
    if (!id) return;
    var base = parseInt(el.getAttribute('data-base-price'), 10);
    previewOne(id).then(function (data) {
      // Only paint if this element still represents the same variant.
      if (el.getAttribute('data-variant-id') !== id) return;
      if (!isFinite(base) && data) base = data.subtotal;
      paint(el, base, data);
    });
  }

  function pdpEl(sectionId) {
    var scope = sectionId
      ? document.getElementById('price-' + sectionId)
      : document;
    return (scope || document).querySelector('[data-auto-discount][data-ad-pdp]');
  }

  var pdpSubscribed = false;
  function initPdp() {
    // Initial paint for every PDP price block on the page.
    document.querySelectorAll('[data-auto-discount][data-ad-pdp]').forEach(paintPdp);

    // Dawn replaces #price-{sectionId}'s innerHTML on variant change and then
    // publishes PUB_SUB_EVENTS.variantChange. The fresh markup carries the new
    // variant's data-variant-id/base-price (server-rendered), so we just repaint.
    if (!pdpSubscribed && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      pdpSubscribed = true;
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var sectionId = event && event.data && event.data.sectionId;
        // Let Dawn finish writing the new innerHTML before we repaint.
        requestAnimationFrame(function () { paintPdp(pdpEl(sectionId)); });
      });
    }
  }

  function init() {
    paintCards();
    initPdp();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-scan when new product cards are injected (Quick Add, AJAX filtering, etc.)
  document.addEventListener('shopify:section:load', init);

  window.GTWAutoDiscount = {
    refresh: init,
    preview: previewOne,
    formatMoney: formatMoney
  };
})();
